import { useParams, Link } from "react-router-dom";
import { GAMES, COACHES, GUIDES, EVENTS, REVIEWS } from "@/lib/mockData";
import CoachCard from "@/components/CoachCard";
import { Star, Users, Trophy, ArrowRight } from "lucide-react";

export default function GameHub() {
  const { slug } = useParams();
  const game = GAMES.find(g => g.slug === slug);
  if (!game) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10"><h1 data-testid="game-hub-empty" className="font-display text-2xl font-bold text-white">Jeu introuvable.</h1><p className="text-slate-400 mt-2">Aucun jeu disponible pour le moment.</p></div>;
  const coaches = COACHES.filter(c => c.gameSlug === game.slug);
  const guides = GUIDES.filter(g => g.game === game.title);
  const events = EVENTS.filter(e => e.game === game.title);

  return (
    <div>
      <div className="game-hub-header">
        <img src={game.slug === "counter-strike-2" ? "/assets/counter-strike-2-guide.jpg" : game.image} alt={game.title} />
        <div className="game-hub-heading">
          <div className="text-xs font-mono uppercase tracking-widest text-white/70 mb-2">{game.genre}</div>
          <h1 data-testid="game-hub-title" className="font-display text-4xl font-semibold">{game.title}</h1>
          <p className="mt-3 text-lg text-slate-200 max-w-2xl">Travaillez vos mécaniques, votre lecture de jeu et votre mental.</p>
          <div className="mt-5 flex flex-wrap gap-4">
            <div className="glass rounded-full px-4 py-2 flex items-center gap-2 text-sm text-white"><Users className="w-4 h-4 text-blue-400" /> {game.coaches} coaches</div>
            <div className="glass rounded-full px-4 py-2 flex items-center gap-2 text-sm text-white"><Trophy className="w-4 h-4 text-amber-400" /> {game.players.toLocaleString()} players</div>
            <div className="glass rounded-full px-4 py-2 flex items-center gap-2 text-sm text-white"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {game.rating}</div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-14">
        <section>
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-2xl lg:text-3xl font-bold text-white">Top Coaches</h2>
            <Link to="/coach" className="text-sm text-slate-300 hover:text-white">Le coach →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(coaches.length ? coaches : COACHES).slice(0,4).map(c => <CoachCard key={c.username} coach={c} />)}
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl lg:text-3xl font-bold text-white mb-6">Popular Guides</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(guides.length ? guides : GUIDES).slice(0,3).map(g => (
              <div key={g.id} className="glass rounded-2xl overflow-hidden card-hover">
                <img src={g.cover} alt={g.title} className="w-full h-40 object-cover" />
                <div className="p-4">
                  <div className="text-xs text-blue-400 uppercase tracking-wider mb-1">{g.type}</div>
                  <div className="font-display font-bold text-white">{g.title}</div>
                  <div className="text-xs text-slate-400 mt-1">{g.author}</div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-1 text-sm"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> {g.rating}</div>
                    <div className="font-bold text-blue-400">€{g.price}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl lg:text-3xl font-bold text-white mb-6">Upcoming Events</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(events.length ? events : EVENTS).slice(0,2).map(e => (
              <div key={e.id} className="glass rounded-2xl overflow-hidden flex card-hover">
                <img src={e.image} alt={e.title} className="w-32 h-full object-cover" />
                <div className="p-4 flex-1">
                  <div className="text-xs text-violet-400 uppercase tracking-wider mb-1">{e.date}</div>
                  <div className="font-display font-bold text-white">{e.title}</div>
                  <div className="text-xs text-slate-400 mt-1">{e.coach}</div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-xs text-slate-400">{e.participants}/{e.max} joined</div>
                    <div className="font-bold text-emerald-400">{e.price === 0 ? "FREE" : `€${e.price}`}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl lg:text-3xl font-bold text-white mb-6">Recent Reviews</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {REVIEWS.slice(0,4).map(r => (
              <div key={r.id} className="glass rounded-2xl p-5">
                <div className="flex gap-1 mb-2">{[...Array(r.rating)].map((_,i) => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-slate-200 text-sm">"{r.text}"</p>
                <div className="mt-3 text-xs text-slate-500">— {r.user}, {r.date}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
